import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { base44 } from '@/api/base44Client';
import { pagesConfig } from '@/pages.config';

const SESSION_KEY = 'pgk_session_id';
const VISIT_COUNT_KEY = 'pgk_visit_count';
const LAST_VISIT_KEY = 'pgk_last_visit';

function getOrCreateSessionId() {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
        sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
        sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
}

function getVisitorInfo() {
    const visitCount = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || '0') + 1;
    const lastVisit = localStorage.getItem(LAST_VISIT_KEY);
    localStorage.setItem(VISIT_COUNT_KEY, String(visitCount));
    localStorage.setItem(LAST_VISIT_KEY, new Date().toISOString());

    const ua = navigator.userAgent;
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(ua);
    const isTablet = /iPad|Tablet/i.test(ua);
    const deviceType = isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop';

    return {
        session_id: getOrCreateSessionId(),
        visit_count: visitCount,
        is_returning: visitCount > 1,
        last_visit: lastVisit || null,
        device_type: deviceType,
        referrer: document.referrer || null,
        user_agent: ua.slice(0, 200),
        screen_width: window.innerWidth,
    };
}

export default function NavigationTracker() {
    const location = useLocation();
    const { isAuthenticated } = useAuth();
    const { Pages, mainPage } = pagesConfig;
    const mainPageKey = mainPage ?? Object.keys(Pages)[0];
    const trackedRef = useRef(new Set());

    useEffect(() => {
        const pathname = location.pathname;
        let pageName;

        if (pathname === '/' || pathname === '') {
            pageName = mainPageKey;
        } else {
            const pathSegment = pathname.replace(/^\//, '').split('/')[0];
            const pageKeys = Object.keys(Pages);
            const matchedKey = pageKeys.find(
                key => key.toLowerCase() === pathSegment.toLowerCase()
            );
            pageName = matchedKey || pathSegment || null;
        }

        if (!pageName) return;

        // Base44 app logs for authenticated users
        if (isAuthenticated) {
            base44.appLogs.logUserInApp(pageName).catch(() => {});
        }

        // Record page view for ALL visitors (guests + logged in)
        const trackKey = `${pageName}-${location.key}`;
        if (trackedRef.current.has(trackKey)) return;
        trackedRef.current.add(trackKey);

        try {
            const visitorInfo = getVisitorInfo();
            base44.entities.PageView.create({
                page: pageName,
                path: pathname,
                session_id: visitorInfo.session_id,
                is_authenticated: !!isAuthenticated,
                is_returning: visitorInfo.is_returning,
                visit_count: visitorInfo.visit_count,
                device_type: visitorInfo.device_type,
                screen_width: visitorInfo.screen_width,
                has_referrer: !!visitorInfo.referrer,
            }).catch(() => {});
        } catch {
            // Silently fail
        }
    }, [location, isAuthenticated, Pages, mainPageKey]);

    return null;
}